import React, { useEffect, useRef } from "react";
import { Num } from "./ui/Num";
import { ROUTE_BY_ID } from "./broadcastEnums";
import { usesEmail } from "./broadcastDraft";
// The formatter lives with the schedule control that owns the input. Importing
// it keeps one definition of "what instant is this, in both zones" — two would
// drift, and the two places it appears are the two places a wrong hour costs
// the most.
import { formatScheduleInstant } from "./SendFooter";

/**
 * The last screen before a broadcast leaves.
 *
 * ---------------------------------------------------------------------------
 * WHAT IT IS FOR
 * ---------------------------------------------------------------------------
 * Sending is irreversible and large. The confirm step exists to state, in
 * sentences rather than in labels, exactly what is about to happen: how many
 * messages, to how many people, how many of those people get hit twice, where
 * the tap lands, and — when scheduled — at what instant in both the author's
 * zone and UTC.
 *
 * Every number is the server's. Nothing here adds, subtracts or infers; see the
 * numbers rule in `SendFooter.jsx`. When there is no estimate at all the dialog
 * says so plainly instead of quietly dropping the sentence: an author who is
 * about to message an unknown number of people deserves to know that the number
 * is unknown, not to see a confident screen with one line missing.
 *
 * Presentational only — no fetching. `onConfirm` is the owner's problem.
 */

const BUTTON_BASE =
  "inline-flex items-center justify-center rounded-lg px-4 py-2 text-[13px] font-medium transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-orange-primary focus-visible:ring-offset-2";

const PRIMARY_BUTTON = "bg-orange-primary text-white hover:bg-[#E85A10]";

const GHOST_BUTTON = "text-bc-text-2 hover:bg-bc-subtle";

const DISABLED_BUTTON = "cursor-not-allowed opacity-50";

const TITLE_ID = "confirm-send-title";

/** Is this a count we can print? `0` is; null / undefined / NaN are not. */
const isCount = (value) => typeof value === "number" && Number.isFinite(value);

/**
 * The pre-send confirmation modal.
 *
 * @param {Object} props
 * @param {boolean} props.open - Renders nothing when false.
 * @param {import("./broadcastDraft").BroadcastDraft} props.draft - The draft about to be sent.
 * @param {{totalSends: number|null, uniquePeople: number|null, bothChannels: number|null}|null} [props.estimate] - The SERVER's estimate, or null when it could not be obtained.
 * @param {boolean} [props.scheduled=false] - Whether this is a scheduled send rather than an immediate one.
 * @param {() => void} [props.onConfirm] - Called when the primary button is pressed.
 * @param {() => void} [props.onCancel] - Called by Cancel, Escape and an overlay click.
 * @param {boolean} [props.submitting=false] - A send is in flight; both buttons disable.
 * @returns {React.ReactElement|null}
 */
export const ConfirmSendDialog = ({
  open,
  draft,
  estimate,
  scheduled = false,
  onConfirm,
  onCancel,
  submitting = false,
}) => {
  const dialogRef = useRef(null);

  // Focus the dialog itself rather than a button: the body is the reason this
  // screen exists, and landing on "Send now" invites confirming without
  // reading it.
  useEffect(() => {
    if (open) dialogRef.current?.focus();
  }, [open]);

  if (!open) return null;

  const email = usesEmail(draft);
  const routeLabel = ROUTE_BY_ID[draft?.routeKey]?.label || "";
  // An email has no destination screen — `route_key` rides along in the payload
  // but nothing taps it. Its destination, if it has one, is the button's link,
  // so that is what this line says instead of claiming the mail opens Home.
  const ctaUrl = String(draft?.emailCtaUrl || "").trim();
  const instant = scheduled ? formatScheduleInstant(draft?.scheduledAtLocal) : null;
  const bothChannels = estimate?.bothChannels;
  const showsBoth = isCount(bothChannels) && bothChannels > 0;

  const handleOverlayClick = (event) => {
    // Only a click on the backdrop itself. Without this, a click that starts
    // inside the dialog and ends on its edge dismisses the confirm step.
    if (event.target === event.currentTarget) onCancel?.();
  };

  const handleKeyDown = (event) => {
    if (event.key === "Escape") onCancel?.();
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/30 p-4"
      onClick={handleOverlayClick}
      onKeyDown={handleKeyDown}
    >
      <div
        ref={dialogRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby={TITLE_ID}
        tabIndex={-1}
        className="w-full max-w-sm rounded-[10px] border border-bc-border bg-white p-5 focus-visible:outline-none"
      >
        <h2 id={TITLE_ID} className="text-base font-semibold text-bc-text">
          {scheduled ? "Schedule this broadcast?" : "Send this broadcast?"}
        </h2>

        <div className="mt-2 flex flex-col gap-1 text-[13px] text-bc-text-2">
          {estimate ? (
            <>
              <p>
                <Num value={estimate.totalSends} /> messages to{" "}
                <Num value={estimate.uniquePeople} /> people.
              </p>
              {showsBoth && (
                <p>
                  <Num value={bothChannels} /> of them get both a push and an
                  email.
                </p>
              )}
            </>
          ) : (
            <p>
              We couldn&apos;t estimate the audience. This will still send to
              everyone in the segments you picked.
            </p>
          )}

          {email ? (
            ctaUrl ? (
              <p className="break-all">Button links to {ctaUrl}</p>
            ) : null
          ) : (
            <p>Opens {routeLabel}.</p>
          )}

          {instant && <p className="text-bc-soft">Sends {instant}</p>}
        </div>

        <div className="mt-5 flex items-center justify-end gap-2">
          <button
            type="button"
            onClick={onCancel}
            disabled={submitting}
            className={`${BUTTON_BASE} ${GHOST_BUTTON} ${submitting ? DISABLED_BUTTON : ""}`}
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={onConfirm}
            disabled={submitting}
            className={`${BUTTON_BASE} ${PRIMARY_BUTTON} ${submitting ? DISABLED_BUTTON : ""}`}
          >
            {submitting ? "Sending…" : scheduled ? "Schedule" : "Send now"}
          </button>
        </div>
      </div>
    </div>
  );
};

export default ConfirmSendDialog;
